import {gql, useQuery} from '@apollo/client';
import {ValidatedTextInputField} from 'common';
import {useDebounce} from 'common/hook';
import {Pane, Popover, Position, TextInputField} from 'evergreen-ui';
import React from 'react';
import {useFormContext} from 'react-hook-form';
import {useCombobox} from 'downshift';

type Provider = {
    id: string;
    guid: string;
    name: string;
};

type ProvidersResult = {
    providers: Provider[];
};

export const PROVIDERS_QUERY = gql`
query providerForUm($name: String) {
  providers(name: $name) {
    id
    guid
    name
  }
}
`;

interface ProviderSelectorProps {
    name?: string;
    label?: React.ReactNode;
    placeholder?: string;
    validationRules?: Record<string, any>;
    onChange?: (guid: string | null) => void;
}

export const ProviderSelector: React.FC<ProviderSelectorProps> = ({ 
    name = 'providerGuid',
    label = <span className='text-base font-normal'>Provider</span>,
    placeholder = 'Search for a provider',
    validationRules = { required: 'This field is required' },
    onChange
}) => {
    const [filterVal, setFilterVal] = React.useState("");
    const debounceValue = useDebounce(filterVal, 300);
    const {
        setValue,
        formState: {
            errors,
        },
    } = useFormContext();
    const [inputItems, setInputItems] = React.useState<Array<Provider>>([]);
    const inputRef = React.useRef<HTMLInputElement>(null);

    const {loading} = useQuery<ProvidersResult, {}>(PROVIDERS_QUERY, {
        variables: {
            name: debounceValue,
        },
        onCompleted: (data: ProvidersResult) => {
            if (data && data.providers) {
                setInputItems(data.providers)
            }
            inputRef.current?.focus();
        },
        fetchPolicy: 'cache-first',
        skip: debounceValue.length < 2,
    });

    const {
        isOpen,
        setInputValue,
        selectedItem,
        getMenuProps,
        getInputProps,
        getComboboxProps,
        highlightedIndex,
        getItemProps,
    } = useCombobox({
        items: inputItems,
        itemToString: (item) => item?.name || '',
        onInputValueChange: ({inputValue}) => {
            setFilterVal(inputValue ?? "");
        },
        onSelectedItemChange: ({selectedItem}) => {
            if (selectedItem) {
                setValue(name, selectedItem.guid, {shouldValidate: true});
                onChange && onChange(selectedItem.guid);
            } else {
                setValue(name, null);
                onChange && onChange(null);
            }
        },
    });

    return (
        <Pane>
            <Popover
                isShown={isOpen}
                position={Position.BOTTOM_LEFT}
                zIndex={1000}
                statelessProps={{
                    style: {
                        zIndex: 1000
                    }
                }}
                onClose={() => {
                    // use for show current selected item, if input value empty clear input value
                    if (selectedItem && filterVal !== "" && inputItems.length !== 0) {
                        setInputValue(selectedItem.name);
                    } else {
                        setInputValue("");
                        setValue(name, null);
                        onChange && onChange(null);
                    }
                }}
                content={
                    <Pane width={320}>
                        {isOpen && loading &&
                            <Pane paddingY={4} paddingLeft={15} className="loading-text">Loading</Pane>}
                        {isOpen &&
                            inputItems.map((item, index) => (
                                <Pane
                                    paddingY={4}
                                    whiteSpace={"nowrap"}
                                    paddingLeft={15}
                                    overflow={'hidden'}
                                    textOverflow={'ellipsis'}
                                    backgroundColor={highlightedIndex === index ? '#bde4ff' :
                                        selectedItem && selectedItem.guid === item.guid ? '#a0aec0' : '#fff'}
                                    key={`${item.guid}${index}`}
                                    {...getItemProps({item, index})}
                                >
                                    {item.name}
                                </Pane>
                            ))}
                        {isOpen && !loading && inputItems.length === 0 &&
                            <Pane paddingY={4} paddingLeft={15}>No providers found</Pane>}
                    </Pane>
                }
                {...getMenuProps()}
            >
                <Pane width="100%">
                    <div {...getComboboxProps()}>
                        <TextInputField
                            label={label}
                            inputHeight={40}
                            inputWidth="100%"
                            width="100%"
                            size={500}
                            placeholder={placeholder}
                            isInvalid={!!errors[name]}
                            data-testid='provider-selector-input'
                            className={loading ? 'loading-cursor' : ''}
                            {...getInputProps({
                                ref: inputRef,
                                onChange: (e: any) => {
                                    if (e.target && e.target.value === "") {
                                        setInputValue("");
                                        setValue(name, null);
                                        onChange && onChange(null);
                                    }
                                },
                            })}
                        />
                    </div>
                </Pane>
            </Popover>
            <ValidatedTextInputField
                label={""}
                className='ev-overwrite ev-input-hidden'
                width="100%"
                inputHeight={40}
                size={500}
                marginTop='-10px'
                name={name}
                data-testid='provider-selector-input-guid'
                validationRules={validationRules}
            />
        </Pane>
    );
};
